// SCR-STAFF-922 — نقش‌ها و دسترسی‌ها (US-IAM-030، 031، 034): نقش‌های سیستمی و سفارشی مرکز با Permissionهای گروه‌بندی‌شده
// بر اساس ماژول؛ Permission وابسته به Feature غیرفعال با نشان «نیازمند ارتقا» (AC-IAM-030.2، BR-IAM-031). AP-IAM-03: ساخت نقش
// خالی یا کپی، ویرایش نام، 2FA و Permissionها (فقط Permissionهای خود — BR-IAM-013)، حذف نقش بی‌عضو، نقش‌های قفل
// غیرقابل ویرایش (BR-IAM-032)، به‌روزرسانی قالب نقش سیستمی ویرایش‌شده با مقایسه (BR-IAM-033) و تب سقف‌های تأیید (US-IAM-034).
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type PermissionCatalogItem, type RoleView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  faDigits,
  FormDialog,
  ListPage,
  LoadingState,
  UpgradeBadge,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { SUBSCRIPTION_PATH } from '../../navigation';
import { WriteNotice } from '../settings/common';
import { ApprovalLimitsTab } from './ApprovalLimitsTab';
import { iamKeys, usePermissionCatalog, useRoleLabel, useRoles } from './common';

export const SCR_STAFF_922 = 'SCR-STAFF-922';

interface PermissionGroup {
  module: string;
  items: { code: string; item: PermissionCatalogItem | undefined }[];
}

/** گروه‌بندی Permissionهای نقش بر اساس ماژول کاتالوگ (بدون کاتالوگ ← پیشوند کد). */
export function groupPermissions(
  permissions: readonly string[],
  catalog: ReadonlyMap<string, PermissionCatalogItem>,
): PermissionGroup[] {
  const groups = new Map<string, PermissionGroup>();
  for (const code of [...permissions].sort()) {
    const item = catalog.get(code);
    const module = item?.module ?? (code.split('.')[0] ?? '').toUpperCase();
    const group = groups.get(module) ?? { module, items: [] };
    group.items.push({ code, item });
    groups.set(module, group);
  }
  return [...groups.values()].sort((a, b) => a.module.localeCompare(b.module));
}

function RolePermissions({
  role,
  catalog,
}: {
  role: RoleView;
  catalog: ReadonlyMap<string, PermissionCatalogItem>;
}) {
  const { t } = useTranslation('iam');
  const groups = groupPermissions(role.permissions, catalog);
  if (groups.length === 0) {
    return <Typography color="text.secondary">{t('iam:roles.no_permissions')}</Typography>;
  }
  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}
      data-testid={`role-permissions-${role.code}`}
    >
      {groups.map((group) => (
        <Box key={group.module} data-module={group.module}>
          <Typography variant="subtitle2" component="h3">
            {t(`iam:modules.${group.module}`, { defaultValue: group.module })}
          </Typography>
          <List dense disablePadding>
            {group.items.map(({ code, item }) => {
              const needsUpgrade = item ? !item.featureEnabled : false;
              return (
                <ListItem
                  key={code}
                  disableGutters
                  data-permission={code}
                  data-upgrade-required={needsUpgrade}
                  secondaryAction={
                    needsUpgrade ? (
                      <UpgradeBadge feature={item?.featureKey ?? undefined} to={SUBSCRIPTION_PATH} />
                    ) : undefined
                  }
                  sx={{ opacity: needsUpgrade ? 0.6 : 1, pe: needsUpgrade ? 18 : 0 }}
                >
                  <ListItemText
                    primary={t(`iam:permission.${code}`, { defaultValue: code })}
                    secondary={<bdi dir="ltr">{code}</bdi>}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
}

type EditorMode = { kind: 'create' } | { kind: 'clone'; role: RoleView } | { kind: 'edit'; role: RoleView };

/** انتخاب Permission گروه‌بندی‌شده؛ افزودن Permissionی که کاربر ندارد ممکن نیست (BR-IAM-013). */
function PermissionPicker({
  catalog,
  selected,
  initial,
  onChange,
}: {
  catalog: readonly PermissionCatalogItem[];
  selected: ReadonlySet<string>;
  initial: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}) {
  const { t } = useTranslation('iam');
  const access = useAccess();
  const byCode = useMemo(() => new Map(catalog.map((item) => [item.code, item])), [catalog]);
  const groups = groupPermissions(
    catalog.map((item) => item.code),
    byCode,
  );
  return (
    <Box data-testid="role-permission-picker">
      <Typography variant="subtitle2" component="h3">
        {t('iam:roles.permissions')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('iam:roles.permissions_hint')} {t('iam:roles.selected', { value: faDigits(selected.size) })}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
        {groups.map((group) => (
          <Box key={group.module} component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
            <Typography component="legend" variant="subtitle2">
              {t(`iam:modules.${group.module}`, { defaultValue: group.module })}
            </Typography>
            {group.items.map(({ code, item }) => {
              const held = access.can(code) || initial.has(code);
              const checked = selected.has(code);
              return (
                <Stack key={code} direction="row" alignItems="center" spacing={1}>
                  <FormControlLabel
                    disabled={!held && !checked}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(event) => {
                          const next = new Set(selected);
                          if (event.target.checked) {
                            next.add(code);
                          } else {
                            next.delete(code);
                          }
                          onChange(next);
                        }}
                        slotProps={{ input: { 'data-permission': code } as object }}
                      />
                    }
                    label={t(`iam:permission.${code}`, { defaultValue: code })}
                    title={held ? undefined : t('iam:roles.not_held')}
                  />
                  {item && !item.featureEnabled && (
                    <UpgradeBadge feature={item.featureKey ?? undefined} to={SUBSCRIPTION_PATH} />
                  )}
                </Stack>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function RoleEditorDialog({
  mode,
  onClose,
  onDone,
}: {
  mode: EditorMode;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const catalog = usePermissionCatalog();
  const roleLabel = useRoleLabel();
  const source = mode.kind === 'create' ? undefined : mode.role;
  const initialPermissions = useMemo(
    () => new Set(mode.kind === 'edit' ? mode.role.permissions : []),
    [mode],
  );
  const [name, setName] = useState(
    mode.kind === 'edit' ? (mode.role.isSystem ? roleLabel(mode.role.code) : (mode.role.name ?? '')) : '',
  );
  const [requiresMfa, setRequiresMfa] = useState(mode.kind === 'edit' ? Boolean(mode.role.requiresMfa) : false);
  const [permissions, setPermissions] = useState<Set<string>>(() => new Set(initialPermissions));
  const [touched, setTouched] = useState(false);
  const [dirty, setDirty] = useState(false);
  const nameEditable = mode.kind !== 'edit' || !mode.role.isSystem;
  const nameInvalid = nameEditable && name.trim().length === 0;

  const save = useMutation({
    mutationFn: async () => {
      if (mode.kind === 'create') {
        return unwrap(
          tenantApi.POST('/api/v1/identity/roles', {
            body: { name: name.trim(), permissions: [...permissions].sort(), requiresMfa },
          }),
        );
      }
      if (mode.kind === 'clone') {
        return unwrap(
          tenantApi.POST('/api/v1/identity/roles/{id}:clone', {
            params: { path: { id: mode.role.roleId } },
            body: { name: name.trim() },
          }),
        );
      }
      const path = { params: { path: { id: mode.role.roleId } } };
      let view = await unwrap(
        tenantApi.PUT('/api/v1/identity/roles/{id}', {
          ...path,
          body: {
            name: mode.role.isSystem ? null : name.trim(),
            requiresMfa: mode.role.mfaMandatory ? null : requiresMfa,
          },
        }),
      );
      const before = [...initialPermissions].sort().join(',');
      const after = [...permissions].sort().join(',');
      if (before !== after) {
        view = await unwrap(
          tenantApi.PUT('/api/v1/identity/roles/{id}/permissions', {
            ...path,
            body: { permissions: [...permissions].sort() },
          }),
        );
      }
      return view;
    },
    onSuccess: (view) => {
      void queryClient.invalidateQueries({ queryKey: iamKeys.roles(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: iamKeys.approvalLimits(session?.tenantId) });
      onDone(
        mode.kind === 'edit' ? t('iam:roles.saved') : t('iam:roles.created', { name: view.name ?? name.trim() }),
      );
    },
  });

  const titleKey =
    mode.kind === 'create'
      ? 'iam:roles.create_title'
      : mode.kind === 'clone'
        ? 'iam:roles.clone_title'
        : 'iam:roles.edit_title';

  return (
    <FormDialog
      open
      titleKey={titleKey}
      screenId={SCR_STAFF_922}
      maxWidth={mode.kind === 'clone' ? 'sm' : 'md'}
      isDirty={dirty}
      isSubmitting={save.isPending}
      submitError={save.error ?? undefined}
      onClose={onClose}
      onSubmit={() => {
        setTouched(true);
        if (!nameInvalid) {
          save.mutate();
        }
      }}
    >
      {source && mode.kind !== 'create' && (
        <Typography color="text.secondary" data-testid="role-editor-source">
          {roleLabel(source.code)}
        </Typography>
      )}
      <TextField
        required={nameEditable}
        disabled={!nameEditable}
        label={t('iam:roles.name')}
        value={name}
        onChange={(event) => {
          setName(event.target.value.slice(0, 100));
          setDirty(true);
        }}
        error={touched && nameInvalid}
        helperText={touched && nameInvalid ? t('iam:roles.name_required') : ' '}
        slotProps={{ htmlInput: { 'data-testid': 'role-name', maxLength: 100 } }}
      />
      {mode.kind !== 'clone' && (
        <>
          {mode.kind === 'edit' && mode.role.mfaMandatory ? (
            <Alert severity="info" data-testid="role-mfa-mandatory">
              {t('iam:roles.requires_mfa_mandatory')}
            </Alert>
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresMfa}
                  onChange={(event) => {
                    setRequiresMfa(event.target.checked);
                    setDirty(true);
                  }}
                  slotProps={{ input: { 'data-testid': 'role-requires-mfa' } as object }}
                />
              }
              label={t('iam:roles.requires_mfa')}
            />
          )}
          {catalog.isPending ? (
            <LoadingState rows={3} />
          ) : catalog.error ? (
            <ErrorState error={catalog.error} compact />
          ) : (
            <PermissionPicker
              catalog={catalog.data?.items ?? []}
              selected={permissions}
              initial={initialPermissions}
              onChange={(next) => {
                setPermissions(next);
                setDirty(true);
              }}
            />
          )}
        </>
      )}
    </FormDialog>
  );
}

/** مقایسه نقش سیستمی ویرایش‌شده با نسخه تازه قالب و اعمال آن (BR-IAM-033 — C-AGENT-382). */
function TemplateDiffDialog({
  role,
  onClose,
  onDone,
}: {
  role: RoleView;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const diff = useQuery({
    queryKey: iamKeys.templateDiff(session?.tenantId, role.roleId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/identity/roles/{id}/template-diff', { params: { path: { id: role.roleId } } }),
      ),
  });
  const apply = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/identity/roles/{id}:apply-template', { params: { path: { id: role.roleId } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: iamKeys.roles(session?.tenantId) });
      onDone(t('iam:roles.updated'));
    },
  });
  const list = (codes: readonly string[], testId: string) => (
    <List dense data-testid={testId}>
      {codes.map((code) => (
        <ListItem key={code} disableGutters>
          <ListItemText
            primary={t(`iam:permission.${code}`, { defaultValue: code })}
            secondary={<bdi dir="ltr">{code}</bdi>}
          />
        </ListItem>
      ))}
    </List>
  );
  const empty = diff.data && diff.data.added.length === 0 && diff.data.removed.length === 0;
  return (
    <Dialog open onClose={apply.isPending ? undefined : onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{t('iam:roles.update_title')}</DialogTitle>
      <DialogContent>
        {diff.isPending ? (
          <LoadingState rows={3} />
        ) : diff.error ? (
          <ErrorState error={diff.error} compact />
        ) : empty ? (
          <Typography>{t('iam:roles.update_none')}</Typography>
        ) : (
          <Stack spacing={1}>
            {(diff.data?.added.length ?? 0) > 0 && (
              <>
                <Typography variant="subtitle2" component="h3">
                  {t('iam:roles.update_added')}
                </Typography>
                {list(diff.data?.added ?? [], 'template-added')}
              </>
            )}
            {(diff.data?.removed.length ?? 0) > 0 && (
              <>
                <Typography variant="subtitle2" component="h3">
                  {t('iam:roles.update_removed')}
                </Typography>
                {list(diff.data?.removed ?? [], 'template-removed')}
              </>
            )}
          </Stack>
        )}
        {apply.error ? <ErrorState error={apply.error} compact /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={apply.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={apply.isPending || !diff.data}
          onClick={() => apply.mutate()}
          data-testid="template-apply"
        >
          {t('iam:roles.apply_update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RolesTab({ onNotice }: { onNotice: (message: string) => void }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const manage = useWriteAccess('iam.role.manage');
  const roleLabel = useRoleLabel();
  const roles = useRoles();
  const catalog = usePermissionCatalog();
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [deleting, setDeleting] = useState<RoleView | null>(null);
  const [reviewing, setReviewing] = useState<RoleView | null>(null);
  const byCode = useMemo(
    () => new Map((catalog.data?.items ?? []).map((item) => [item.code, item])),
    [catalog.data],
  );

  const remove = useMutation({
    mutationFn: (role: RoleView) =>
      unwrap(tenantApi.DELETE('/api/v1/identity/roles/{id}', { params: { path: { id: role.roleId } } })),
    onSuccess: () => {
      setDeleting(null);
      onNotice(t('iam:roles.deleted'));
      void queryClient.invalidateQueries({ queryKey: iamKeys.roles(session?.tenantId) });
      void queryClient.invalidateQueries({ queryKey: iamKeys.approvalLimits(session?.tenantId) });
    },
  });

  const done = (message: string) => {
    setEditor(null);
    setReviewing(null);
    onNotice(message);
  };

  let body;
  if (roles.isPending) {
    body = <LoadingState rows={5} />;
  } else if (roles.error) {
    body = <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />;
  } else if ((roles.data?.items.length ?? 0) === 0) {
    body = <EmptyState titleKey="iam:roles.empty" />;
  } else {
    body = (
      <Box>
        {(roles.data?.items ?? []).map((role) => {
          const disabled = role.permissions.filter((code) => byCode.get(code)?.featureEnabled === false).length;
          const members = Number(role.memberCount ?? 0);
          const editable = manage.canWrite && !role.isLocked;
          return (
            <Accordion key={role.roleId} disableGutters data-testid={`role-${role.code}`}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Typography sx={{ fontWeight: 600 }}>{roleLabel(role.code)}</Typography>
                  {role.isSystem ? (
                    <Chip size="small" variant="outlined" label={<bdi dir="ltr">{role.code}</bdi>} />
                  ) : (
                    <Chip size="small" color="primary" variant="outlined" label={t('iam:roles.custom')} />
                  )}
                  {role.isSystem && <Chip size="small" label={t('iam:roles.system')} />}
                  {role.isLocked && <Chip size="small" icon={<LockOutlinedIcon />} label={t('iam:roles.locked')} />}
                  {role.isCustomized && (
                    <Chip size="small" variant="outlined" label={t('iam:roles.customized')} />
                  )}
                  {role.requiresMfa && (
                    <Chip size="small" variant="outlined" label={t('iam:roles.mfa')} data-testid={`role-mfa-${role.code}`} />
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {t('iam:roles.permission_count', { value: faDigits(role.permissions.length) })}
                  </Typography>
                  {role.memberCount !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      {t('iam:roles.members', { value: faDigits(members) })}
                    </Typography>
                  )}
                  {disabled > 0 && (
                    <Typography variant="body2" color="secondary.main" data-testid={`role-upgrade-${role.code}`}>
                      {t('iam:roles.upgrade_count', { value: faDigits(disabled) })}
                    </Typography>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {role.templateUpdateVersion !== null && role.templateUpdateVersion !== undefined && (
                    <Alert
                      severity="info"
                      data-testid={`role-template-update-${role.code}`}
                      action={
                        manage.canWrite ? (
                          <Button color="inherit" onClick={() => setReviewing(role)} data-testid="template-review">
                            {t('iam:roles.review_update')}
                          </Button>
                        ) : undefined
                      }
                    >
                      {t('iam:roles.update_available', { value: faDigits(role.templateUpdateVersion) })}
                    </Alert>
                  )}
                  {manage.permitted && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        disabled={!editable}
                        onClick={() => setEditor({ kind: 'edit', role })}
                        data-testid={`role-edit-${role.code}`}
                      >
                        {t('iam:roles.edit')}
                      </Button>
                      {role.code !== 'TenantOwner' && (
                        <Button
                          size="small"
                          startIcon={<ContentCopyOutlinedIcon />}
                          disabled={!manage.canWrite}
                          onClick={() => setEditor({ kind: 'clone', role })}
                          data-testid={`role-clone-${role.code}`}
                        >
                          {t('iam:roles.clone')}
                        </Button>
                      )}
                      {!role.isSystem && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          disabled={!manage.canWrite}
                          onClick={() => {
                            remove.reset();
                            setDeleting(role);
                          }}
                          data-testid={`role-delete-${role.code}`}
                        >
                          {t('iam:roles.delete')}
                        </Button>
                      )}
                    </Stack>
                  )}
                  <RolePermissions role={role} catalog={byCode} />
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {manage.permitted && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <WriteNotice write={manage} />
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            disabled={!manage.canWrite}
            onClick={() => setEditor({ kind: 'create' })}
            data-testid="role-create"
            sx={{ ms: 'auto' }}
          >
            {t('iam:roles.create')}
          </Button>
        </Stack>
      )}
      {catalog.error ? <ErrorState error={catalog.error} compact /> : null}
      {body}
      {editor && <RoleEditorDialog mode={editor} onClose={() => setEditor(null)} onDone={done} />}
      {reviewing && <TemplateDiffDialog role={reviewing} onClose={() => setReviewing(null)} onDone={done} />}
      <ConfirmDialog
        open={deleting !== null}
        titleKey="iam:roles.delete_title"
        messageKey="iam:roles.delete_message"
        messageValues={{ name: deleting ? roleLabel(deleting.code) : '' }}
        confirmKey="iam:roles.delete"
        destructive
        busy={remove.isPending}
        error={remove.error ?? undefined}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </Stack>
  );
}

type RolesPageTab = 'roles' | 'limits';

export function RolesPage() {
  const { t } = useTranslation('iam');
  const access = useAccess();
  // سقف‌ها با iam.role.manage (GET approval-limits)؛ مشاهده نقش‌ها با iam.member.view.
  const limitsVisible = access.can('iam.role.manage');
  const [selected, setTab] = useState<RolesPageTab>('roles');
  const tab = limitsVisible ? selected : 'roles';
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <ListPage screenId={SCR_STAFF_922} titleKey="iam:roles.title" subtitleKey="iam:roles.subtitle">
      {limitsVisible && (
        <Tabs
          value={tab}
          onChange={(_, value: RolesPageTab) => setTab(value)}
          sx={{ mb: 2 }}
          aria-label={t('iam:roles.title')}
        >
          <Tab value="roles" label={t('iam:roles.tabs.roles')} data-testid="roles-tab-roles" />
          <Tab value="limits" label={t('iam:roles.tabs.limits')} data-testid="roles-tab-limits" />
        </Tabs>
      )}
      {tab === 'roles' ? <RolesTab onNotice={setNotice} /> : <ApprovalLimitsTab onNotice={setNotice} />}
      <Snackbar
        open={notice !== null}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        message={notice}
        data-testid="roles-notice"
      />
    </ListPage>
  );
}
